import React, { useState, useEffect, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { createSelector } from 'reselect';
import { joyDocHelper, tableHelper, generateObjectId } from '@builttocreate/engine-utils';
import { applyLogic } from '@joyfill/conditional-logic';

import Page from './Page';
import FieldList from './fields/FieldList';
import PageList from './PageList';
import LoadingIndicator from '../misc/LoadingIndicator';
import Icon from '../misc/Icon';

import InputSettingsField from './settings/InputSettingsField';
import TableEditorSettings from './settings/TableEditorSettings';
import FieldSettings, { TableColumnSettings } from './settings/FieldSettings';
import PageSettings from './settings/PageSettings';
import FileSettings from './settings/FileSettings';

import FieldTypes from '../../constants/FieldTypes';
import FieldModes from '../../constants/FieldModes';
import FileViews from '../../constants/FileViews';
import FieldDisplayTypes from '../../constants/FieldDisplayTypes';
import FieldListTypes from '../../constants/FieldListTypes';
import FieldPresentationModes from '../../constants/FieldPresentationModes';

import ThemeContext from '../../theme/ThemeContext';
import ThemeIcons from '../../theme/Icons';

const { getColumnOrder, getRowOrder } = tableHelper;
const { sortPages } = joyDocHelper;

const StyledWrapper = styled.div`
  height: 100%;
  display: flex;
  flex-direction: row;
  flex: 1;
  font-family: ${(props) => props.theme.fontFamily};

  * {
    font-family: ${(props) => props.theme.fontFamily} !important;
  }

`;

const StyledLoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

/**
 * >>>>>>>>> Start Left Panel Styles <<<<<<<<<<<
 */
const StyledLeftPanelWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 220px;
  min-width: 215px;
  margin-right: 8px;
  background-color: ${(props) => props.theme.backgroundColor};
  ${(props) => `border: ${props.theme.borderWidth}px solid ${props.theme.borderColor};`}
  border-radius: ${(props) => props.theme.borderRadius}px;
`;

const StyledSearchableFieldListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow-y: auto;

  ::-webkit-scrollbar {
    display:none; //hide scrollbar in chrome
  }
  scrollbar-width:none; // hide scrollbar in firefox
  -ms-overflow-style: none; // hide scroll bar in edge

`;

const StyledSearchInputWrapper = styled.div`
  padding: 16px;
`;

const StyledEmptyPlaceHolderWrapper = styled.div`
  width:100%;
  margin-top: 0px;
  text-align:center;
  font-weight; normal;
  font-size: 12px;
  color: ${(props) => props.theme.fontColorSecondary}
`;

const StyledFieldListContainer = styled.div`
  display: flex;
  flex: 1;
`;

/**
 * >>>>>>>>> End Left Panel Styles <<<<<<<<<<<
 */

/**
 * >>>>>>>>> Start Right Panel Styles <<<<<<<<<<<
 */
const StyledRightPanelWrapper = styled.div`
  width: 220px;
  min-width: 200px;
  margin-left: 8px;
  overflow-y: auto;
  overflow-x: hidden;
  background-color: ${(props) => props.theme.backgroundColor};
  ${(props) => `border: ${props.theme.borderWidth}px solid ${props.theme.borderColor};`}
  border-radius: ${(props) => props.theme.borderRadius}px;

`;

/**
 * >>>>>>>>> End Right Panel Styles <<<<<<<<<<<
 */

/**
 * >>>>>>>>> Start Middle Panel Styles <<<<<<<<<<<
 */
const StyledMiddlePanel = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  margin-right: auto;
  margin-left: auto;
`;

const StyledMiddlePanelBody = styled.div`
  display: flex;
  flex: 1;
  justify-content: center;
  background-color: ${(props) => props.theme.backgroundColor};
  ${(props) => `border: ${props.theme.borderWidth}px solid ${props.theme.borderColor};`}
  border-radius: ${(props) => props.theme.borderRadius}px;
  overflow-y: auto;
  overflow-x: hidden;

`;

const StyledMiddlePanelTableSettings = styled.div`
  margin-top: 4px;
`;

const StyledMiddlePanelPageBarWrapper = styled.div`
  max-width: 100%;
  background-color: ${(props) => props.theme.backgroundColor};
  display: flex;
  flex-direction: row;
  ${(props) => (props.isMobileView ? 'margin-bottom: 4px;' : 'margin-top: 4px;')};
  ${(props) => `border: ${props.theme.borderWidth}px solid ${props.theme.borderColor};`}
  border-radius: ${(props) => props.theme.borderRadius}px;
`;

const StyledMiddlePanelLoader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
`;
/**
 * >>>>>>>>> End Middle Panel Styles <<<<<<<<<<<
 */

const File = ({

  mode = FieldModes.fill,

  /**
   * Document properties
   */
  fields, //Raw fields returned on doc

  /**
   * File Properties
   */
  id,
  name,

  width,
  height,

  styles = {},
  pages,
  pageOrder,

  /**
   * Overwrite and default select a page and field via prop
   */
  initialPageId, //Overwrite internal selected page
  initialFieldId, //Overwrite internal selected field

  /**
   * Array of field options that can be added to the
   * form. These display inside the field list on
   * the left side of the form builder.
   */
  fieldOptions,

  /**
   * Field settings config object
   */
  fieldSettings,

  /**
   * Determine if field lazy loading is enabled or disabled
   */
  enableLazyLoadFields,

  /**
   * File action handler
   */
  onFileChange,
  onFileUpload,

  /**
   * Page change(s) action handler
   */
  onPageCreate,
  onPageChange,
  onPageUpload,
  onPageUploadDelete,
  onPageDelete,
  onPageDuplicate,

  /**
   * Field change(s) action handler
   */
  onFieldCreate,
  onFieldDelete,
  onFieldUpload,
  onFieldChange,
  onFieldRowAddBelow,
  onFieldRowDuplicate,
  onFieldRowMoveUp,
  onFieldRowMoveDown,
  onFieldRowCellChange,
  onFieldRowCellUpload,
  onFieldRowDelete,
  onFieldColumnDelete,

  onFocus,
  onBlur,

  /**
   * onFieldLayoutsChange is fired with entire array of field positions
   * anytime any layout values change.
   */
  onFieldLayoutsChange,

  /**
   * View Helpers
   */
  view,
  views,
  onViewChange,
  onToggleViewSync,

  /**
   * Mock monitor for testing. I couldn't figure out any
   * reasonable way to mock the monitor in order to
   * get a client offset value that is valid and testable.
   * Without the mock the client offset always returns
   * x and y values as undefined.
   */
  mockTestDndMonitor, //Only for testing

}) => {

  const theme = useContext(ThemeContext);

  /**
   * >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
   * >>>>> START VIEW HELPERS <<<<<
   * >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
   */

  /**
   * Mobile view detects if the screen should be optimized
   * for mobile forms experience. The mobile view experience is
   * optimized for fill and read-only modes.
   */
  const isMobileView = view === FileViews.mobile && mode !== FieldModes.edit;

  /**
   * >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
   * >>>>> END VIEW HELPERS <<<<<<<
   * >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
   */

  /**
   * >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
   * >>>>> START PAGE HELPERS <<<<<
   * >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
   */

  const [pageLoading, setPageLoading] = useState(false);
  const [activePageSettingsPageId, setActivePageSettingsPageId] = useState(null);

  const sortedPages = applyLogicAndSortPagesSelector({ pages, pageOrder, fields, mode });

  const [pageId, setPageId] = useState(getActivePageIdSelector({ sortedPages, initialPageId }));
  const activePage = getPageByIdSelector({ sortedPages, pageId });
  const activePageIndex = getActivePageIndexSelector({ sortedPages, pageId });

  /**
   * Why do we force a page rerender when these properties change?
   *
   * 1. We have to force react-grid-layout to rerender when layouts,
   *    cols or rowHeights are changed so that the grid works properly.
   * 2. We have to force a rerender so that PDF Forms will generate
   *    a new percentage difference and resize the background image and
   *    field appropriatly based on the new width/height properties.
   */
  useEffect(() => {
    setPageLoading(true);
    setTimeout(() => setPageLoading(false), 100);
  }, [view, width, height, activePage?.layout, activePage?.cols, activePage?.rowHeight]);

  /**
   * This specifies what width to use for parent element that contains
   * the form, page navigation, and everything else within the middle
   * panel for all the different states of the SDK.
   */
  const getContainerWidth = () => {

    /**
     * Edit mode will use the width property for the primary view but
     * when mobile view is enabled it will use the default mobileWidth;
     */
    if (mode === FieldModes.edit) return view === FileViews.mobile ? 460 : width;
    else return width;

  };

  /**
   * How to handle active page:
   *
   * There are two methods to have a field become the currently selected active field:
   * - Method 1: Page Id is externally changed and passed to SDK via initialPageId
   * - Method 2: User manually selects active page
   */

  /**
   * Method 1: Enable active page to be controlled via initialPageId prop. This allows
   * a user of the component to pass in a initialPageId and set it as active
   * and overwrite the current internal state;
   */
  useEffect(() => {
    if (initialPageId) {
      setPageId(initialPageId);
      setActivePageSettingsPageId(initialPageId);
    }
  }, [initialPageId]);

  /**
   * Method 2: User manually selects active page
   */
  const handlePageSelect = (nextActivePageId) => {

    /**
     * If page is currently active and page settings are already displayed for active page then do nothing.
     */
    if (nextActivePageId === pageId && activePageSettingsPageId === nextActivePageId) return;

    /**
     * Remove active field states from previous page.
     */
    setActiveFieldId(null);
    setActiveTableColumnSettingsColumnId(null);
    setPageId(nextActivePageId);
    setActivePageSettingsPageId(nextActivePageId);

    /**
     * Important pageLoading Description
     *
     * Why do we set a loading state for the page and then quickly set it to not be in a loading state?
     *
     * This is to force the GridLayout to be unmounted and remounted fresh. We are required
     * to set internal field state inside the GridLayout and this flushes all state and
     * UI to load the newly selected page with no internal state conflicts.
     */
    setPageLoading(true);
    setTimeout(() => setPageLoading(false), 100);

  };

  const handlePageCreate = useCallback(() => {
    if (onPageCreate) onPageCreate(id);
  }, [id]);

  const handlePageDuplicate = useCallback((pageId) => {
    if (onPageDuplicate) onPageDuplicate(id, pageId);
  }, [id]);

  const handlePageDelete = useCallback((pageId) => {

    const targetDeletePageIndex = sortedPages.findIndex((page) => pageId === page._id);
    const nextActivePageIndex = targetDeletePageIndex === 0 ? targetDeletePageIndex + 1 : targetDeletePageIndex - 1;
    const nextActivePageId = sortedPages[nextActivePageIndex]._id;
    setPageId(nextActivePageId);

    if (onPageDelete) onPageDelete(id, pageId);

  }, [id, pageId, sortedPages]);

  const handlePageChange = useCallback((pageId, changes) => {
    if (onPageChange) onPageChange(id, pageId, changes);
  }, [id, pageId, activePage?.fields]);

  const handlePageUpload = useCallback((pageId, file) => {
    if (onPageUpload) onPageUpload(id, pageId, file);
  }, [id]);

  const handlePageUploadDelete = useCallback((pageId) => {
    if (onPageUploadDelete) onPageUploadDelete(id, pageId);
  }, [id]);

  /**
   * >>>>>>>>>>>>>>>>>>>>>>>>>>>>
   * >>>>> END PAGE HELPERS <<<<<
   * >>>>>>>>>>>>>>>>>>>>>>>>>>>>
   */

  /**
   * >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
   * >>>>> START FIELD HELPERS <<<<<
   * >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
   */
  const [activeFieldId, setActiveFieldId] = useState(initialFieldId);
  const activePageFields = activePage ? applyLogicForPageFields({ pageFields: activePage.fields, fields, mode }) : [];
  const activeField = activePage ? getFieldByIdSelector({ fields: activePage.fields, fieldId: activeFieldId }) : null;

  /**
   * Enable active field to be controlled via initialFieldId prop. This allows
   * a user of the component to pass in a initialFieldId and set it as active
   * and overwrite the current internal state;
   */
  useEffect(() => {
    if (initialFieldId) setActiveFieldId(initialFieldId);
  }, [initialFieldId]);

  const handleSetActiveFieldId = (fieldId) => {

    if (activeFieldId === fieldId) return;

    setActiveTableColumnSettingsColumnId(null);
    setActivePageSettingsPageId(null);
    setActiveFieldId(fieldId);
  };

  /**
   * How to handle active fields:
   *
   * There are two methods to have a field become the currently selected active field:
   * - Method 1: Newly added form field becomes selected active field
   * - Method 2: User manually selects active field
   */

  //Method 1: Newly added field becomes selected active field
  useEffect(() => {
    const pageFields = activePageFields;
    const newestField = pageFields[pageFields.length - 1];
    if (activeFieldId && newestField) handleSetActiveFieldId(newestField._id);
  }, [activePageFields.length]);

  //Method 2: User manually selects active field
  const handleFieldSelect = useCallback((fieldId) => {
    handleSetActiveFieldId(fieldId);
  }, [pageId]);

  /**
   * What activeTableColumnSettingsColumnId is used for:
   *
   * activeTableFieldColumnSettingsColumnId is used to determine when and what
   * column settings to display in the settings panel.
   */
  const [activeTableColumnSettingsColumnId, setActiveTableColumnSettingsColumnId] = useState(null);

  /**
   * This creation handler represents the creation of a new
   * field position on the form.
   *
   * The fieldPosition._id that gets added to the object is meant to be used as the
   * fieldPosition._id and not the field._id.
   *
   * What is the difference between fieldPosition and field? I'm glad you asked!
   *
   * fieldPosition
   * - Represents the style and placement coordinates on the form.
   * - There could be multiple fieldPositions for a single field if you're supporting multiple form views.
   *
   * field
   * - Represents the actual field data portion. This is the value, configs, etc.
   */
  const handleFieldCreate = useCallback((fieldPosition) => {

    const newFieldPosition = { ...fieldPosition, _id: generateObjectId() };

    /**
     * We don't want to save a placeholder description at this time with the field.
     * In the future a description value will be used to display explainer
     * text below the field.
     */
    if (newFieldPosition.description) delete newFieldPosition.description;

    const fieldId = generateObjectId();

    if (!newFieldPosition.field) newFieldPosition.field = fieldId;

    if (!newFieldPosition.identifier) newFieldPosition.identifier = `field_${fieldId}`;

    if (!newFieldPosition.displayType) newFieldPosition.displayType = FieldDisplayTypes.original;

    if (fieldPosition && fieldPosition.tableColumns && Array.isArray(fieldPosition.tableColumns)) {

      const newTableColumns = [...fieldPosition.tableColumns].map((column) => {

        const nextColumn = { ...column };

        const columnId = generateObjectId();
        if (!nextColumn._id) nextColumn._id = columnId;
        if (!nextColumn.identifier) nextColumn.identifier = `field_column_${columnId}`;
        if (!nextColumn.width) nextColumn.width = 0;
        if (!newFieldPosition.columnType && newFieldPosition.column && newFieldPosition.column === column._id) {
          newFieldPosition.columnType = column.type;
        }

        return nextColumn;

      });

      newFieldPosition.tableColumns = newTableColumns;

      if (!newFieldPosition.tableColumnOrder) newFieldPosition.tableColumnOrder = getColumnOrder(null, newTableColumns);

    }

    if (fieldPosition.type === FieldTypes.table) {
      //Ensure rowOrder is properly added to newly created fields
      if (fieldPosition.value && !fieldPosition.rowOrder) newFieldPosition.rowOrder = getRowOrder(null, fieldPosition.value);
    }

    /**
     * Logic below hides field title for newly added fields  by default if it's a pdf form
     */
    if (activePage && activePage.presentation === FieldPresentationModes.transparent) {
      newFieldPosition.titleDisplay = 'none';
    }

    if (onFieldCreate) onFieldCreate(id, pageId, newFieldPosition);

  }, [id, pageId, activePage]);

  const handleFieldChange = useCallback((fieldId, fieldIdentifier, fieldPositionId, data) => {

    if (onFieldChange) onFieldChange(id, pageId, fieldId, fieldIdentifier, fieldPositionId, data);

  }, [id, pageId]);

  const handleFieldUpload = useCallback((fieldId, fieldIdentifier, fieldPositionId, files) => {

    if (onFieldUpload) onFieldUpload(id, pageId, fieldId, fieldIdentifier, fieldPositionId, files);

  }, [id, pageId]);

  const handleFieldDelete = useCallback((fieldId, fieldIdentifier, fieldPositionId) => {

    if (onFieldDelete) onFieldDelete(id, pageId, fieldId, fieldIdentifier, fieldPositionId);

  }, [id, pageId]);

  const handleLayoutChange = useCallback((positions) => {

    if (onFieldLayoutsChange) onFieldLayoutsChange(id, pageId, positions);

  }, [id, pageId]);

  const handleFieldRowCellChange = useCallback((fieldId, fieldIdentifier, fieldPositionId, rowId, columnId, change) => {

    if (onFieldRowCellChange) onFieldRowCellChange(id, pageId, fieldId, fieldIdentifier, fieldPositionId, rowId, columnId, change);

  }, [id, pageId]);

  const handleFieldRowCellUpload = useCallback((fieldId, fieldIdentifier, fieldPositionId, rowId, columnId, change) => {
    if (onFieldRowCellUpload) onFieldRowCellUpload(id, pageId, fieldId, fieldIdentifier, fieldPositionId, rowId, columnId, change);
  }, [id, pageId]);

  const handleFieldRowAddBelow = useCallback((fieldId, fieldIdentifier, fieldPositionId, rowIndex) => {

    if (onFieldRowAddBelow) onFieldRowAddBelow(id, pageId, fieldId, fieldIdentifier, fieldPositionId, rowIndex);

  }, [id, pageId]);

  const handleFieldRowDuplicate = useCallback((fieldId, fieldIdentifier, fieldPositionId, rowId, rowIndex) => {

    if (onFieldRowDuplicate) onFieldRowDuplicate(id, pageId, fieldId, fieldIdentifier, fieldPositionId, rowId, rowIndex);

  }, [id, pageId]);

  const handleFieldRowMoveUp = useCallback((fieldId, fieldIdentifier, fieldPositionId, rowIndex) => {

    if (onFieldRowMoveUp) onFieldRowMoveUp(id, pageId, fieldId, fieldIdentifier, fieldPositionId, rowIndex);

  }, [id, pageId]);

  const handleFieldRowMoveDown = useCallback((fieldId, fieldIdentifier, fieldPositionId, rowIndex) => {

    if (onFieldRowMoveDown) onFieldRowMoveDown(id, pageId, fieldId, fieldIdentifier, fieldPositionId, rowIndex);

  }, [id, pageId]);

  const handleFieldRowDelete = useCallback((fieldId, fieldIdentifier, fieldPositionId, rowIndex) => {

    if (onFieldRowDelete) onFieldRowDelete(id, pageId, fieldId, fieldIdentifier, fieldPositionId, rowIndex);

  }, [id, pageId]);

  const handleFieldColumnDelete = useCallback((fieldId, fieldIdentifier, fieldPositionId, columnId) => {

    setActiveTableColumnSettingsColumnId(null);

    if (onFieldColumnDelete) onFieldColumnDelete(id, pageId, fieldId, fieldIdentifier, fieldPositionId, columnId);

  }, [id, pageId]);

  const handleFocus = useCallback((fieldId, fieldIdentifier, fieldPositionId, e, rowId, columnId, columnIdentifier) => {

    if (onFocus) onFocus(id, pageId, fieldId, fieldIdentifier, fieldPositionId, e, rowId, columnId, columnIdentifier);

  }, [id, pageId]);

  const handleBlur = useCallback((fieldId, fieldIdentifier, fieldPositionId, e, rowId, columnId, columnIdentifier) => {

    if (onBlur) onBlur(id, pageId, fieldId, fieldIdentifier, fieldPositionId, e, rowId, columnId, columnIdentifier);

  }, [id, pageId]);

  /**
   * >>>>>>>>>>>>>>>>>>>>>>>>>>>>>
   * >>>>> END FIELD HELPERS <<<<<
   * >>>>>>>>>>>>>>>>>>>>>>>>>>>>>
   */

  /**
   * >>>>>>>>>>>>>>>>>>>>>>>>>>>>>
   * >>>>> START FILE HELPERS <<<<<
   * >>>>>>>>>>>>>>>>>>>>>>>>>>>>>
   */

  /**
   * Reset state when file is changed
   */
  useEffect(() => {

    setPageId(getActivePageIdSelector({ sortedPages, initialPageId }));
    setActivePageSettingsPageId(null);
    setActiveFieldId(null);
    setActiveTableColumnSettingsColumnId(null);

    /**
     * Important pageLoading Description
     *
     * Why do we set a loading state for the page and then quickly set it to not be in a loading state?
     *
     * This is to force the GridLayout to be unmounted and remounted fresh. We are required
     * to set internal field state inside the GridLayout and this flushes all state and
     * UI to load the newly selected page with no internal state conflicts.
     */
    setPageLoading(true);
    setTimeout(() => setPageLoading(false), 100);

  }, [id]);

  /**
   * >>>>>>>>>>>>>>>>>>>>>>>>>>>>>
   * >>>>> END FILE HELPERS <<<<<
   * >>>>>>>>>>>>>>>>>>>>>>>>>>>>>
   */

  if (!activePage || !pageId) {
    return (<StyledLoadingWrapper><LoadingIndicator size="lg" /></StyledLoadingWrapper>);
  }

  const tableEditorSettingsNode = mode === FieldModes.edit && (activeField && (activeField.type === FieldTypes.table || activeField.type === FieldTypes.inputGroup)) ? (
    <StyledMiddlePanelTableSettings>
      <TableEditorSettings
        display
        id={activeField ? activeField._id : 'pending-create'}
        activeFieldPosition={activeField}
        onChange={handleFieldChange}
        onColumnEdit={(fieldId, columnId) => setActiveTableColumnSettingsColumnId(columnId)}
        view={view}
        disableDragAndDrop={view === FileViews.mobile || activeField?.type === FieldTypes.inputGroup}
      />
    </StyledMiddlePanelTableSettings>
  ) : null;

  const tableColumnSettingsNode = !activeTableColumnSettingsColumnId || !activeField ||
  (activeField.type !== FieldTypes.table && activeField.type !== FieldTypes.inputGroup)
    ? null : (
      <TableColumnSettings
        {...activeField}
        config={fieldSettings}
        activeFieldPosition={activeField}
        columnId={activeTableColumnSettingsColumnId}
        onChange={handleFieldChange}
        onFieldColumnDelete={handleFieldColumnDelete}
      />
    );

  const pageNavNode = (
    <StyledMiddlePanelPageBarWrapper theme={theme} isMobileView={isMobileView}>
      <PageList
        view={view}
        mode={mode}
        sortedPages={sortedPages}
        activePageId={pageId}
        onSelect={handlePageSelect}
        onDelete={handlePageDelete}
        onDuplicate={handlePageDuplicate}
        onCreate={handlePageCreate}
        onViewChange={onViewChange}
        onFileSettingsClick={() => {
          setActivePageSettingsPageId(null);
          handleSetActiveFieldId(null);
        }}
      />
    </StyledMiddlePanelPageBarWrapper>
  );

  return (
    <>
      <DndProvider backend={HTML5Backend}>
        <StyledWrapper theme={theme} data-testid="file">
          {mode !== FieldModes.edit || isMobileView ? null : (
            <StyledLeftPanelWrapper theme={theme}>
              <SearchableFieldList fieldOptions={fieldOptions} />
            </StyledLeftPanelWrapper>
          )}
          <StyledMiddlePanel
            style={{
              /**
               * Right now this is used to ensure that the page navigation bar is the same
               * width as the middle panel form. Without this the page navigation bar will
               * extend pass the desired width and cause horizontal scrolling issues.
               */
              width: getContainerWidth(),
              maxWidth: getContainerWidth(),
              height,
              maxHeight: height
            }}
          >
            {isMobileView ? pageNavNode : null}
            <StyledMiddlePanelBody theme={theme}>
              {pageLoading ? (<StyledMiddlePanelLoader />) : (
                <Page
                  {...{

                    /**
                     * Why -62?
                     *
                     * 62px represents the required height used by the page nav bar element (plus page nav
                     * scrollbar height) itself, the margin between the page nav bar and the form page above,
                     * and the border widths used by the form page above.
                     */
                    containerHeight: height - 62,
                    containerWidth: getContainerWidth(),

                    mode,
                    view,

                    id: activePage._id,
                    ...activePage,
                    fields: activePageFields,

                    focused: activeFieldId,
                    fieldDefaultStyles: styles,

                    onFieldChange: handleFieldChange,
                    onFieldSelect: handleFieldSelect,
                    onFieldCreate: handleFieldCreate,
                    onFieldUpload: handleFieldUpload,
                    onFieldLayoutsChange: handleLayoutChange,
                    onFieldColumnDelete: handleFieldColumnDelete,
                    onTableFieldCellUpdate: handleFieldRowCellChange,
                    onTableFieldCellUpload: handleFieldRowCellUpload,
                    onTableFieldRowAddBelow: handleFieldRowAddBelow,
                    onTableFieldRowDuplicate: handleFieldRowDuplicate,
                    onTableFieldRowMoveUp: handleFieldRowMoveUp,
                    onTableFieldRowMoveDown: handleFieldRowMoveDown,
                    onTableFieldRowDelete: handleFieldRowDelete,
                    onFocus: handleFocus,
                    onBlur: handleBlur,

                    enableLazyLoadFields,
                    mockTestDndMonitor,
                  }}
                />
              )}
            </StyledMiddlePanelBody>
            {tableEditorSettingsNode}
            {isMobileView ? null : pageNavNode}
          </StyledMiddlePanel>
          {mode !== FieldModes.edit || isMobileView ? null : (
            <StyledRightPanelWrapper theme={theme}>
              {tableColumnSettingsNode}
              {!activeField || activeTableColumnSettingsColumnId ? null : (
                <FieldSettings
                  fileId={id}
                  view={view}
                  mode={mode}
                  {...activeField}
                  sortedPages={sortedPages}
                  fields={fields}
                  config={fieldSettings}
                  activeFieldPosition={activeField}
                  onChange={handleFieldChange}
                  onUpload={handleFieldUpload}
                  onDelete={handleFieldDelete}
                  fieldDefaultStyles={styles}
                  fieldMode={activePage.presentation}
                />
              )}
              {!activePageSettingsPageId ? null : (
                <PageSettings
                  fileId={id}
                  view={view}
                  pageIndex={activePageIndex}
                  page={activePage}
                  sortedPages={sortedPages}
                  fields={fields}
                  config={fieldSettings?.page}
                  hidden={activePage?.hidden}
                  onChange={handlePageChange}
                  onDelete={pages.length < 2 ? null : handlePageDelete}
                  onUpload={handlePageUpload}
                  onUploadDelete={handlePageUploadDelete}
                />
              )}
              {!activeField && !activePageSettingsPageId ? (
                <FileSettings
                  id={id}
                  view={view}
                  name={name}
                  styles={styles}
                  config={fieldSettings?.file}
                  onChange={onFileChange}
                  onUpload={onFileUpload}
                  mode={mode}
                  fieldMode={activePage.presentation}
                  views={views}
                  onToggleViewSync={onToggleViewSync}
                />
              ) : null}
            </StyledRightPanelWrapper>
          )}
        </StyledWrapper>
      </DndProvider>
    </>
  );

};

File.propTypes = {
  mode: PropTypes.string,
  id: PropTypes.string, //File Id
  name: PropTypes.string,
  styles: PropTypes.object,
  defaultPageId: PropTypes.string,
  views: PropTypes.any,
  pages: PropTypes.arrayOf(PropTypes.shape({
    _id: PropTypes.string,
    layout: PropTypes.string,
    presentation: PropTypes.string,
    width: PropTypes.number,
    height: PropTypes.number,
    cols: PropTypes.number,
    rowHeight: PropTypes.number,
    fields: PropTypes.array,
    padding: PropTypes.number,
    margin: PropTypes.number,
    borderWidth: PropTypes.number,
    borderColor: PropTypes.string,
    borderStyle: PropTypes.string,
    backgroundImage: PropTypes.string,
  })).isRequired,
  pageOrder: PropTypes.array,
  onFileChange: PropTypes.func,
  onFileUpload: PropTypes.func,
  onPageChange: PropTypes.func,
  onPageCreate: PropTypes.func,
  onPageUpload: PropTypes.func,
  onPageUploadDelete: PropTypes.func,
  onFieldCreate: PropTypes.func,
  onFieldChange: PropTypes.func,
  onFieldUpload: PropTypes.func,
  onFieldDelete: PropTypes.func,
  onFieldLayoutsChange: PropTypes.func,
  onViewChange: PropTypes.func,
  onToggleViewSync: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func
};

export const SearchableFieldList = ({ fieldOptions }) => {

  const [nextFields, setNextFields] = useState(fieldOptions);
  const theme = useContext(ThemeContext);

  const handleSearchQueryChange = (value) => {

    /**
     * We are transforming the first character of the search query to uppercase
     * since our fieldTitles begin with an uppercase Character (ex: Image, Short Text etc)
     */
    const nextSearchQuery = value.toLowerCase();

    //Logic to reset state incase search query is empty
    if (!nextSearchQuery || nextSearchQuery.length < 1) {

      setNextFields(fieldOptions);

      return null;

    }

    /**
    * Logic to merge fieldGroup.fields along with other fields
    * so that they are included in the list of fields to search
    * from when search Query is present.
    *
    * Sample Structure of field groups:
    *  {
          type: FieldListTypes.fieldGroup,
          title: 'Display Fields',
          open: true,
          fields: [
            {
              title: 'Image',
              type: FieldTypes.image,
              description: 'Logo or graphic'
            },
          ]
        },
    */
    const fieldGroupFields = [];

    fieldOptions.forEach((item) => {

      if (item.type === FieldListTypes.fieldGroup
        && item.fields
        && item.fields.length > 0) {

        item.fields.forEach((field) => fieldGroupFields.push(field));

      }

    });

    const nextFieldOptions = [...fieldOptions, ...fieldGroupFields];

    const matchingFields = nextFieldOptions.filter((item) => {

      const nextTitle = item.title.replace(/\s/g, '').toLowerCase();

      if (nextTitle === nextSearchQuery || nextTitle.includes(nextSearchQuery)) return item;

      return null;

    });

    setNextFields(matchingFields);

  };

  return (
    <StyledSearchableFieldListWrapper>
      <StyledSearchInputWrapper>
        <InputSettingsField
          onChangeText={handleSearchQueryChange}
          addonBefore={<Icon color={theme.fontColorPrimary} icon={ThemeIcons.misc.search} size="sm" />}
        />
      </StyledSearchInputWrapper>
      {nextFields && nextFields.length > 0 ? (
        <StyledFieldListContainer>
          <FieldList fields={nextFields} />
        </StyledFieldListContainer>
      ) : (<StyledEmptyPlaceHolderWrapper theme={theme}> No fields found</StyledEmptyPlaceHolderWrapper>)}
    </StyledSearchableFieldListWrapper>
  );
};

export const getFieldLookupSelector = createSelector(
  (state) => state.fields,
  (fields) => {
    const lookup = {};
    fields.forEach((field) => lookup[field._id] = field);
    return lookup;
  }
);

export const getFieldByIdSelector = createSelector(
  getFieldLookupSelector,
  (state) => state.fieldId,
  (lookup, fieldId) => {
    return lookup[fieldId];
  }
);

const getActivePageIdSelector = createSelector(
  (state) => state.sortedPages,
  (state) => state.initialPageId,
  (sortedPages, initialPageId) => {

    if (!sortedPages || sortedPages.length < 1) return null;

    const pageIndex = !initialPageId ? -1 : sortedPages.findIndex((page) => initialPageId === page._id);

    return pageIndex === -1 ? sortedPages[0]._id : sortedPages[pageIndex]._id;

  }
);

export const getPageLookupSelector = createSelector(
  (state) => state.sortedPages,
  (sortedPages) => {
    const lookup = {};
    sortedPages.forEach((page) => lookup[page._id] = page);
    return lookup;
  }
);

export const getPageByIdSelector = createSelector(
  getPageLookupSelector,
  (state) => state.pageId,
  (lookup, pageId) => {
    return lookup[pageId];
  }
);

export const getActivePageIndexSelector = createSelector(
  (state) => state.sortedPages,
  (state) => state.pageId,
  (sortedPages, pageId) => {
    return sortedPages.findIndex((page) => page._id === pageId);
  }
);

export const getSortedPagesSelector = createSelector(
  (state) => state.pageOrder,
  (state) => state.pages,
  (pageOrder, pages) => {
    return sortPages(pages, pageOrder);
  }
);

export const applyLogicAndSortPagesSelector = createSelector(
  getSortedPagesSelector,
  (state) => state.fields,
  (state) => state.mode,
  (pages, fields, mode) => {

    if (mode === FieldModes.edit) return pages;

    return applyLogic(pages, fields, '_id').filter((item) => !item.hidden);

  }
);

export const applyLogicForPageFields = createSelector(
  (state) => state.pageFields,
  (state) => state.fields,
  (state) => state.mode,
  (pageFields, fields, mode) => {

    if (mode === FieldModes.edit) return pageFields;

    return applyLogic(pageFields, fields, '_id').filter((item) => !item.hidden);

  }
);

export default React.memo(File);
